import Immutable from 'immutable';


// New formula for calculating satisfaction score:
//
// * Group by provider ID/customer ID
// * Grouping by customer ID means we count each customer once, and pick the last non-null promoter value
// * “Customers added” is the number of distinct customers (asked if they would recommend)
// * “Yes” is the number of distinct customers that would recommend (promoter = true)
// * “No” is the number of distinct customers that would not recommend (promoter = false)
// * Response rate is ((promoter + detractor) / customers), a value between 0 and 1
// * Maximum response rate is the maximum of the above, for all provider IDs (including null)
// * Satisfaction score is (((promoter - detractor) / customers) / max response rate)
// * For example, if a provider has 30 customer asks, 7 promoters, 2 detractors, their response rate is ((7 + 2) / 30) = 0.3
// * If the maximum response rate is 0.36, their satisfaction score is (((7 - 2) / 30) / 0.36) = 46
// * Value shown is rounded, no decimal point, min 0, max 100
// * Entry for each non-null provider, showing provider name, sorted by satisfaction score (highest score at the top)
// * Entry for the null provider, only if there are customers, name is “Other”, shown after all non-null providers
// * Entry for the business, name is “Overall”, grouped by customer ID only (not provider ID)

export default function Scorecard(classifieds) {
  const classifiedsByProvider = Immutable.List(classifieds).groupBy(getProvider);
  const activitiesByProvider  = classifiedsByProvider.map(getActivitiesForProvider);
  const scorecards            = classifiedsByProvider.map(getScorecard);
  const maxResponseRate       = scorecards.map(scorecard => scorecard.responseRate).max();

  const providers = scorecards
    .map(addSatisfactionScore)
    .map(addActivities)
    .map(addProvider)
    .sort(compareBy('satisfactionScore', 'customerCount', 'promoterCount', 'detractorCount'))
    .valueSeq()
    .toJS();

  const overall = getOverall(classifieds, maxResponseRate);

  return { providers, overall };

  function addSatisfactionScore(scorecard) {
    const customerCount  = scorecard.customerCount;
    const promoterCount  = scorecard.promoterCount;
    const detractorCount = scorecard.detractorCount;

    const satisfactionScore = getSatisfactionScore({
      customerCount,
      promoterCount,
      detractorCount,
      maxResponseRate
    });

    return { ...scorecard, satisfactionScore };
  }

  function addActivities(scorecard, provider) {
    const activities = activitiesByProvider.get(provider);
    return { ...scorecard, activities };
  }

  function addProvider(scorecard, provider) {
    return provider.merge(scorecard);
  }

}


function getProvider(classified) {
  if (classified.provider)
    return Immutable.Map(classified.provider);
  else
    return Immutable.Map({ id: '', name: 'Other' });
}


function getActivitiesForProvider(classifieds) {
  return classifieds
    .filter(hasPromoterValue)
    .map(classified => {
      const { customer }  = classified;
      const { feedback }  = classified;
      const { review }    = classified;
      const { promoter }  = feedback;
      const { timestamp } = classified;

      return Immutable.Map({ customer, promoter, timestamp, feedback, review });
    });
}



function getScorecard(classifieds) {
  const dedupedClassifieds = classifieds
    .groupBy(classified => classified.customer.id)
    .map(getMostRecentClassified)
    .valueSeq();

  const customerCount  = dedupedClassifieds.size;
  const promoterCount  = dedupedClassifieds.count(isPromoter);
  const detractorCount = dedupedClassifieds.count(isDetractor);
  const responseRate   = getResponseRate({ promoterCount, detractorCount, customerCount });

  const providerScorecard = {
    customerCount,
    promoterCount,
    detractorCount,
    responseRate
  };

  return providerScorecard;
}

function getMostRecentClassified(classifieds) {
  return classifieds
    .filter(hasPromoterValue)
    .maxBy(classified => classified.timestamp);
}

function getResponseRate({ promoterCount, detractorCount, customerCount }) {
  const responseRate = (promoterCount + detractorCount) / customerCount;
  return responseRate;
}



function getOverall(classifieds, maxResponseRate) {
  const overall = Immutable.List(classifieds)
    .groupBy(classified => classified.customer.id)
    .map(getScorecard)
    .reduce((results, scorecard) => {
      const customerCount  = scorecard.customerCount + results.customerCount;
      const promoterCount  = scorecard.promoterCount + results.promoterCount;
      const detractorCount = scorecard.detractorCount + results.detractorCount;

      return { promoterCount, detractorCount, customerCount };
    }, { customerCount: 0, promoterCount: 0, detractorCount: 0 });

  const satisfactionScore            = getSatisfactionScore({ ...overall, maxResponseRate });
  const overallWithSatisfactionScore = { ...overall, satisfactionScore };

  return overallWithSatisfactionScore;
}

function getSatisfactionScore({ promoterCount, detractorCount, customerCount, maxResponseRate }) {
  const satisfactionScore = Math.max(((promoterCount - detractorCount) / customerCount / maxResponseRate * 100), 0);

  const score = Math.round(satisfactionScore);

  if (isNaN(score))
    return 0;
  else
    return score;
}


function hasPromoterValue({ feedback } = {}) {
  return feedback && typeof feedback.promoter !== 'undefined';
}

function isPromoter({ feedback } = {}) {
  return feedback && feedback.promoter === true;
}

function isDetractor({ feedback } = {}) {
  return feedback && feedback.promoter === false;
}


function compareBy(...fields) {
  const comparator = fields.reverse().reduce(function(next, field) {
    return compareByField(field, next);
  }, null);
  return comparator;
}

function compareByField(field, next) {
  return function(a, b) {
    const value1 = a.get(field);
    const value2 = b.get(field);

    return (value2 - value1) || next && next(a, b);
  };
}
